import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeader, formatDateTimeDDMMYYYY } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { 
  BookOpen, Plus, Search, Star, StarOff, Trash2, Edit, Clock, User, Lock, Unlock,
  ChevronRight, ChevronDown, FileText, FolderPlus, FilePlus, Share2, MoreVertical,
  Bold, Italic, Underline, List, ListOrdered, Link, Tag, X, Users, Globe, Settings,
  Palette, Eye, EyeOff
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";

const NOTEBOOK_COLORS = [
  { value: "blue", label: "Blue", bg: "bg-blue-500", light: "bg-blue-100", text: "text-blue-700" },
  { value: "green", label: "Green", bg: "bg-green-500", light: "bg-green-100", text: "text-green-700" },
  { value: "purple", label: "Purple", bg: "bg-purple-500", light: "bg-purple-100", text: "text-purple-700" },
  { value: "red", label: "Red", bg: "bg-red-500", light: "bg-red-100", text: "text-red-700" },
  { value: "orange", label: "Orange", bg: "bg-orange-500", light: "bg-orange-100", text: "text-orange-700" },
  { value: "teal", label: "Teal", bg: "bg-teal-500", light: "bg-teal-100", text: "text-teal-700" },
  { value: "pink", label: "Pink", bg: "bg-pink-500", light: "bg-pink-100", text: "text-pink-700" },
];

const SECTION_COLORS = [
  { value: "default", label: "Default", bg: "bg-slate-500" },
  { value: "blue", label: "Blue", bg: "bg-blue-500" },
  { value: "green", label: "Green", bg: "bg-green-500" },
  { value: "yellow", label: "Yellow", bg: "bg-yellow-500" },
  { value: "red", label: "Red", bg: "bg-red-500" },
  { value: "purple", label: "Purple", bg: "bg-purple-500" },
  { value: "orange", label: "Orange", bg: "bg-orange-500" },
  { value: "teal", label: "Teal", bg: "bg-teal-500" },
];

const PAGE_COLORS = [
  { value: "white", label: "White", bg: "bg-white", css: "#ffffff" },
  { value: "cream", label: "Cream", bg: "bg-amber-50", css: "#fffbeb" },
  { value: "light-blue", label: "Light Blue", bg: "bg-blue-50", css: "#eff6ff" },
  { value: "light-green", label: "Light Green", bg: "bg-green-50", css: "#f0fdf4" },
  { value: "light-yellow", label: "Light Yellow", bg: "bg-yellow-50", css: "#fefce8" },
  { value: "light-pink", label: "Light Pink", bg: "bg-pink-50", css: "#fdf2f8" },
  { value: "light-purple", label: "Light Purple", bg: "bg-purple-50", css: "#faf5ff" },
  { value: "light-gray", label: "Light Gray", bg: "bg-slate-50", css: "#f8fafc" },
];

const NotesPanel = ({ user }) => {
  // Data states
  const [notebooks, setNotebooks] = useState([]);
  const [sections, setSections] = useState([]);
  const [pages, setPages] = useState([]);
  const [recentPages, setRecentPages] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allTags, setAllTags] = useState([]);
  
  // Selection states
  const [selectedNotebook, setSelectedNotebook] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [expandedNotebooks, setExpandedNotebooks] = useState({});
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [viewMode, setViewMode] = useState("notebooks"); // notebooks, recent, favorites, search
  
  // Dialog states
  const [notebookDialogOpen, setNotebookDialogOpen] = useState(false);
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [pageDialogOpen, setPageDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  
  // Form states
  const [editingNotebook, setEditingNotebook] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editingPage, setEditingPage] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  const [newNotebook, setNewNotebook] = useState({ name: "", color: "blue", is_personal: true });
  const [newSection, setNewSection] = useState({ name: "", color: "default" });
  const [newPage, setNewPage] = useState({ title: "", content: "", tags: [], page_color: "white" });
  const [lockPassword, setLockPassword] = useState("");
  const [shareSettings, setShareSettings] = useState({ users: [], teams: [] });
  const [tagInput, setTagInput] = useState("");
  
  // Content editor ref
  const contentRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef(null);

  // Teams list
  const teams = ['Account', 'Processing', 'BackOffice', 'ITDept', 'Partners'];

  // Fetch all data
  useEffect(() => {
    fetchNotebooks();
    fetchRecentPages();
    if (user?.role === 'admin') {
      fetchAllUsers();
    }
  }, [user]);

  const fetchNotebooks = async () => {
    try {
      const response = await axios.get(`${API}/notebooks`, { headers: getAuthHeader() });
      setNotebooks(response.data);
    } catch (error) {
      toast.error("Failed to fetch notebooks");
    } finally {
      setLoading(false);
    }
  };

  const fetchSections = async (notebookId) => {
    try {
      const response = await axios.get(`${API}/sections?notebook_id=${notebookId}`, { headers: getAuthHeader() });
      setSections(response.data);
    } catch (error) {
      toast.error("Failed to fetch sections");
    }
  };

  const fetchPages = async (sectionId) => {
    try {
      const response = await axios.get(`${API}/pages?section_id=${sectionId}`, { headers: getAuthHeader() });
      setPages(response.data);
    } catch (error) {
      toast.error("Failed to fetch pages");
    }
  };

  const fetchRecentPages = async () => {
    try {
      const response = await axios.get(`${API}/pages/recent?limit=15`, { headers: getAuthHeader() });
      setRecentPages(response.data);
    } catch (error) {
      console.error("Failed to fetch recent pages");
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-users`, { headers: getAuthHeader() });
      setAllUsers(response.data.filter(u => u.id !== user?.id));
    } catch (error) {
      console.error("Failed to fetch users");
    }
  };

  // Search functionality
  useEffect(() => {
    const searchPages = async () => {
      if (searchTerm.length < 2) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }
      
      setIsSearching(true);
      try {
        const response = await axios.get(`${API}/pages/search?q=${encodeURIComponent(searchTerm)}`, { headers: getAuthHeader() });
        setSearchResults(response.data);
        setViewMode("search");
      } catch (error) {
        console.error("Search failed");
      } finally {
        setIsSearching(false);
      }
    };

    const debounce = setTimeout(searchPages, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm]);

  // Notebook operations
  const handleCreateNotebook = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/notebooks`, newNotebook, { headers: getAuthHeader() });
      toast.success("Notebook created!");
      setNotebookDialogOpen(false);
      setNewNotebook({ name: "", color: "blue", is_personal: true });
      fetchNotebooks();
    } catch (error) {
      toast.error("Failed to create notebook");
    }
  };

  const handleUpdateNotebook = async (e) => {
    e.preventDefault();
    if (!editingNotebook) return;
    try {
      await axios.put(`${API}/notebooks/${editingNotebook.id}`, {
        name: editingNotebook.name,
        color: editingNotebook.color,
        is_personal: editingNotebook.is_personal
      }, { headers: getAuthHeader() });
      toast.success("Notebook updated!");
      setEditingNotebook(null);
      setNotebookDialogOpen(false);
      fetchNotebooks();
    } catch (error) {
      toast.error("Failed to update notebook");
    }
  };

  const handleDeleteNotebook = async () => {
    if (!deleteTarget || deleteTarget.type !== 'notebook') return;
    try {
      await axios.delete(`${API}/notebooks/${deleteTarget.id}`, { headers: getAuthHeader() });
      toast.success("Notebook deleted!");
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
      if (selectedNotebook?.id === deleteTarget.id) {
        setSelectedNotebook(null);
        setSelectedSection(null);
        setSelectedPage(null);
      }
      fetchNotebooks();
    } catch (error) {
      toast.error("Failed to delete notebook");
    }
  };

  // Section operations
  const handleCreateSection = async (e) => {
    e.preventDefault();
    if (!selectedNotebook) return;
    try {
      await axios.post(`${API}/sections`, {
        ...newSection,
        notebook_id: selectedNotebook.id
      }, { headers: getAuthHeader() });
      toast.success("Section created!");
      setSectionDialogOpen(false);
      setNewSection({ name: "", color: "default" });
      fetchSections(selectedNotebook.id);
    } catch (error) {
      toast.error("Failed to create section");
    }
  };

  const handleUpdateSection = async (e) => {
    e.preventDefault();
    if (!editingSection) return;
    try {
      await axios.put(`${API}/sections/${editingSection.id}`, {
        name: editingSection.name,
        color: editingSection.color
      }, { headers: getAuthHeader() });
      toast.success("Section updated!");
      setEditingSection(null);
      setSectionDialogOpen(false);
      fetchSections(selectedNotebook.id);
    } catch (error) {
      toast.error("Failed to update section");
    }
  };

  const handleDeleteSection = async () => {
    if (!deleteTarget || deleteTarget.type !== 'section') return;
    try {
      await axios.delete(`${API}/sections/${deleteTarget.id}`, { headers: getAuthHeader() });
      toast.success("Section deleted!");
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
      if (selectedSection?.id === deleteTarget.id) {
        setSelectedSection(null);
        setSelectedPage(null);
      }
      fetchSections(selectedNotebook.id);
    } catch (error) {
      toast.error("Failed to delete section");
    }
  };

  // Page operations
  const handleCreatePage = async (e) => {
    e.preventDefault();
    if (!selectedSection || !selectedNotebook) return;
    try {
      const response = await axios.post(`${API}/pages`, {
        ...newPage,
        section_id: selectedSection.id,
        notebook_id: selectedNotebook.id
      }, { headers: getAuthHeader() });
      toast.success("Page created!");
      setPageDialogOpen(false);
      setNewPage({ title: "", content: "", tags: [] });
      fetchPages(selectedSection.id);
      setSelectedPage(response.data);
    } catch (error) {
      toast.error("Failed to create page");
    }
  };

  const handleDeletePage = async () => {
    if (!deleteTarget || deleteTarget.type !== 'page') return;
    try {
      await axios.delete(`${API}/pages/${deleteTarget.id}`, { headers: getAuthHeader() });
      toast.success("Page deleted!");
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
      if (selectedPage?.id === deleteTarget.id) {
        setSelectedPage(null);
      }
      if (selectedSection) {
        fetchPages(selectedSection.id);
      }
      fetchRecentPages();
    } catch (error) {
      toast.error("Failed to delete page");
    }
  };

  // Auto-save page content
  const savePageContent = useCallback(async (pageId, content) => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/pages/${pageId}`, { content }, { headers: getAuthHeader() });
      fetchRecentPages();
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setIsSaving(false);
    }
  }, []);

  const handleContentChange = (e) => {
    const content = e.target.value;
    setSelectedPage(prev => ({ ...prev, content }));
    
    // Debounced auto-save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      if (selectedPage?.id) {
        savePageContent(selectedPage.id, content);
      }
    }, 1000);
  };

  const handlePageTitleChange = async (newTitle) => {
    if (!selectedPage) return;
    try {
      await axios.put(`${API}/pages/${selectedPage.id}`, { title: newTitle }, { headers: getAuthHeader() });
      setSelectedPage(prev => ({ ...prev, title: newTitle }));
      if (selectedSection) {
        fetchPages(selectedSection.id);
      }
      fetchRecentPages();
    } catch (error) {
      toast.error("Failed to update title");
    }
  };

  const handleToggleFavorite = async (page) => {
    try {
      const response = await axios.post(`${API}/pages/${page.id}/favorite`, {}, { headers: getAuthHeader() });
      if (selectedPage?.id === page.id) {
        setSelectedPage(prev => ({ ...prev, is_favorite: response.data.is_favorite }));
      }
      if (selectedSection) {
        fetchPages(selectedSection.id);
      }
      fetchRecentPages();
      toast.success(response.data.is_favorite ? "Added to favorites" : "Removed from favorites");
    } catch (error) {
      toast.error("Failed to update favorite status");
    }
  };

  // Lock/Unlock notebook
  const handleLockNotebook = async (e) => {
    e.preventDefault();
    if (!selectedNotebook) return;
    try {
      await axios.put(`${API}/notebooks/${selectedNotebook.id}`, {
        is_locked: true,
        lock_password: lockPassword
      }, { headers: getAuthHeader() });
      toast.success("Notebook locked!");
      setLockDialogOpen(false);
      setLockPassword("");
      fetchNotebooks();
    } catch (error) {
      toast.error("Failed to lock notebook");
    }
  };

  const handleUnlockNotebook = async (e) => {
    e.preventDefault();
    if (!selectedNotebook) return;
    try {
      await axios.post(`${API}/notebooks/${selectedNotebook.id}/unlock?password=${encodeURIComponent(lockPassword)}`, {}, { headers: getAuthHeader() });
      // Temporarily unlock in UI
      setSelectedNotebook(prev => ({ ...prev, is_locked: false }));
      toast.success("Notebook unlocked!");
      setUnlockDialogOpen(false);
      setLockPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid password");
    }
  };

  // Share notebook
  const handleShareNotebook = async (e) => {
    e.preventDefault();
    if (!selectedNotebook) return;
    try {
      await axios.post(`${API}/notebooks/${selectedNotebook.id}/share?${shareSettings.users.map(u => `user_ids=${u}`).join('&')}&${shareSettings.teams.map(t => `team_names=${t}`).join('&')}`, {}, { headers: getAuthHeader() });
      toast.success("Sharing settings updated!");
      setShareDialogOpen(false);
      setShareSettings({ users: [], teams: [] });
      fetchNotebooks();
    } catch (error) {
      toast.error("Failed to update sharing");
    }
  };

  // Tag management
  const handleAddTag = () => {
    if (tagInput.trim() && !newPage.tags.includes(tagInput.trim())) {
      setNewPage(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tag) => {
    setNewPage(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const handleUpdatePageTags = async (pageId, tags) => {
    try {
      await axios.put(`${API}/pages/${pageId}`, { tags }, { headers: getAuthHeader() });
      setSelectedPage(prev => ({ ...prev, tags }));
      toast.success("Tags updated!");
    } catch (error) {
      toast.error("Failed to update tags");
    }
  };

  // Select notebook
  const handleSelectNotebook = async (notebook) => {
    // Check if locked
    if (notebook.is_locked && notebook.owner_id === user?.id) {
      setSelectedNotebook(notebook);
      setUnlockDialogOpen(true);
      return;
    }
    
    setSelectedNotebook(notebook);
    setSelectedSection(null);
    setSelectedPage(null);
    setViewMode("notebooks");
    await fetchSections(notebook.id);
    
    // Auto-expand
    setExpandedNotebooks(prev => ({ ...prev, [notebook.id]: true }));
  };

  // Select section
  const handleSelectSection = async (section) => {
    setSelectedSection(section);
    setSelectedPage(null);
    await fetchPages(section.id);
  };

  // Select page
  const handleSelectPage = async (page) => {
    try {
      const response = await axios.get(`${API}/pages/${page.id}`, { headers: getAuthHeader() });
      setSelectedPage(response.data);
      
      // If coming from recent/search, also set notebook and section
      if (!selectedNotebook || selectedNotebook.id !== page.notebook_id) {
        const nb = notebooks.find(n => n.id === page.notebook_id);
        if (nb) {
          setSelectedNotebook(nb);
          await fetchSections(nb.id);
        }
      }
    } catch (error) {
      toast.error("Failed to load page");
    }
  };

  // Get color classes
  const getNotebookColorClasses = (color) => {
    const c = NOTEBOOK_COLORS.find(nc => nc.value === color);
    return c || NOTEBOOK_COLORS[0];
  };

  const getSectionColorClass = (color) => {
    const c = SECTION_COLORS.find(sc => sc.value === color);
    return c?.bg || "bg-slate-500";
  };

  const getPageColorClasses = (color) => {
    const c = PAGE_COLORS.find(pc => pc.value === color);
    return c || PAGE_COLORS[0];
  };

  // Update page color
  const handleUpdatePageColor = async (pageId, newColor) => {
    try {
      await axios.put(`${API}/pages/${pageId}`, { page_color: newColor }, { headers: getAuthHeader() });
      setSelectedPage(prev => ({ ...prev, page_color: newColor }));
      if (selectedSection) {
        fetchPages(selectedSection.id);
      }
      toast.success("Page color updated!");
    } catch (error) {
      toast.error("Failed to update page color");
    }
  };

  // Rich text formatting
  const editorRef = useRef(null);

  const applyFormatting = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      editorRef.current.focus();
    }
  };

  const handleEditorInput = () => {
    if (editorRef.current && selectedPage) {
      const content = editorRef.current.innerHTML;
      setSelectedPage(prev => ({ ...prev, content }));
      
      // Debounced auto-save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        savePageContent(selectedPage.id, content);
      }, 1000);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return formatDateTimeDDMMYYYY(dateString);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="flex h-[calc(100vh-120px)] bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Left Sidebar - Notebooks & Sections */}
        <div className="w-72 border-r border-slate-200 flex flex-col bg-slate-50">
          {/* Search Bar */}
          <div className="p-3 border-b border-slate-200">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search all notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 bg-white"
              />
            </div>
          </div>

          {/* Quick Access */}
          <div className="p-2 border-b border-slate-200">
            <Button
              variant={viewMode === "recent" ? "secondary" : "ghost"}
              size="sm"
              className="w-full justify-start"
              onClick={() => { setViewMode("recent"); fetchRecentPages(); }}
            >
              <Clock className="w-4 h-4 mr-2" />
              Recent Pages
            </Button>
            <Button
              variant={viewMode === "favorites" ? "secondary" : "ghost"}
              size="sm"
              className="w-full justify-start"
              onClick={async () => {
                setViewMode("favorites");
                try {
                  const response = await axios.get(`${API}/pages?favorites_only=true`, { headers: getAuthHeader() });
                  setSearchResults(response.data);
                } catch (error) {
                  console.error("Failed to fetch favorites");
                }
              }}
            >
              <Star className="w-4 h-4 mr-2" />
              Favorites
            </Button>
          </div>

          {/* Notebooks List */}
          <ScrollArea className="flex-1">
            <div className="p-2">
              <div className="flex items-center justify-between mb-2 px-2">
                <span className="text-xs font-semibold text-slate-500 uppercase">Notebooks</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => { setEditingNotebook(null); setNotebookDialogOpen(true); }}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>New Notebook</TooltipContent>
                </Tooltip>
              </div>

              {notebooks.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-sm">
                  <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No notebooks yet</p>
                  <Button 
                    variant="link" 
                    size="sm"
                    onClick={() => setNotebookDialogOpen(true)}
                  >
                    Create your first notebook
                  </Button>
                </div>
              ) : (
                notebooks.map(notebook => {
                  const colorClasses = getNotebookColorClasses(notebook.color);
                  const isExpanded = expandedNotebooks[notebook.id];
                  const isOwner = notebook.owner_id === user?.id;
                  
                  return (
                    <div key={notebook.id} className="mb-1 group">
                      <Collapsible
                        open={isExpanded}
                        onOpenChange={(open) => setExpandedNotebooks(prev => ({ ...prev, [notebook.id]: open }))}
                      >
                        <div
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${
                            selectedNotebook?.id === notebook.id ? 'bg-slate-200' : 'hover:bg-slate-100'
                          }`}
                          onClick={() => handleSelectNotebook(notebook)}
                        >
                          <CollapsibleTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" className="h-5 w-5 p-0">
                              {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                            </Button>
                          </CollapsibleTrigger>
                          
                          <div className={`w-3 h-3 rounded ${colorClasses.bg}`} />
                          
                          <span className="flex-1 text-sm font-medium truncate">{notebook.name}</span>
                          
                          {notebook.is_locked && <Lock className="w-3 h-3 text-amber-500" title="Locked" />}
                          {!notebook.is_personal && <Users className="w-3 h-3 text-blue-500" title="Shared" />}
                          
                          {isOwner && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-50 hover:opacity-100 group-hover:opacity-100">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={() => { setEditingNotebook(notebook); setNotebookDialogOpen(true); }}>
                                  <Edit className="w-4 h-4 mr-2" /> Edit Notebook
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => { setSelectedNotebook(notebook); setShareDialogOpen(true); setShareSettings({ users: notebook.shared_with || [], teams: notebook.shared_with_teams || [] }); }}>
                                  <Share2 className="w-4 h-4 mr-2" /> Share with Teams
                                </DropdownMenuItem>
                                {notebook.is_personal && (
                                  <DropdownMenuItem onClick={() => { setSelectedNotebook(notebook); notebook.is_locked ? setUnlockDialogOpen(true) : setLockDialogOpen(true); }}>
                                    {notebook.is_locked ? <Unlock className="w-4 h-4 mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                                    {notebook.is_locked ? "Unlock Notebook" : "Lock Notebook"}
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={() => { setDeleteTarget({ type: 'notebook', id: notebook.id, name: notebook.name }); setDeleteConfirmOpen(true); }}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>

                        <CollapsibleContent>
                          {selectedNotebook?.id === notebook.id && sections.map(section => (
                            <div
                              key={section.id}
                              className={`group/section flex items-center gap-2 ml-6 px-2 py-1.5 rounded cursor-pointer transition-colors ${
                                selectedSection?.id === section.id ? 'bg-slate-200' : 'hover:bg-slate-100'
                              }`}
                              onClick={() => handleSelectSection(section)}
                            >
                              <div className={`w-1.5 h-4 rounded-full ${getSectionColorClass(section.color)}`} />
                              <span className="flex-1 text-sm truncate">{section.name}</span>
                              
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-50 hover:opacity-100 group-hover/section:opacity-100">
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-40">
                                  <DropdownMenuItem onClick={() => { setEditingSection(section); setSectionDialogOpen(true); }}>
                                    <Edit className="w-4 h-4 mr-2" /> Edit Section
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    className="text-red-600"
                                    onClick={() => { setDeleteTarget({ type: 'section', id: section.id, name: section.name }); setDeleteConfirmOpen(true); }}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          ))}
                          
                          {selectedNotebook?.id === notebook.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="ml-6 w-[calc(100%-1.5rem)] justify-start text-slate-500 hover:text-slate-700"
                              onClick={() => { setEditingSection(null); setSectionDialogOpen(true); }}
                            >
                              <FolderPlus className="w-3 h-3 mr-2" />
                              Add Section
                            </Button>
                          )}
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Middle - Pages List */}
        <div className="w-64 border-r border-slate-200 flex flex-col bg-white">
          <div className="p-3 border-b border-slate-200 flex items-center justify-between">
            <span className="font-medium text-sm text-slate-700">
              {viewMode === "recent" ? "Recent Pages" : 
               viewMode === "favorites" ? "Favorites" : 
               viewMode === "search" ? "Search Results" :
               selectedSection?.name || "Pages"}
            </span>
            {selectedSection && viewMode === "notebooks" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => { setEditingPage(null); setNewPage({ title: "", content: "", tags: [], page_color: "white" }); setPageDialogOpen(true); }}
                  >
                    <FilePlus className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>New Page</TooltipContent>
              </Tooltip>
            )}
          </div>

          <ScrollArea className="flex-1">
            <div className="p-2">
              {/* Show pages based on view mode */}
              {(() => {
                const displayPages = viewMode === "recent" ? recentPages :
                                    viewMode === "favorites" || viewMode === "search" ? searchResults :
                                    pages;
                
                if (displayPages.length === 0) {
                  return (
                    <div className="text-center py-8 text-slate-500 text-sm">
                      <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>{viewMode === "search" ? "No results found" : "No pages yet"}</p>
                    </div>
                  );
                }

                return displayPages.map(page => (
                  <div
                    key={page.id}
                    className={`p-2 rounded-lg cursor-pointer transition-colors mb-1 ${
                      selectedPage?.id === page.id ? 'bg-purple-50 border border-purple-200' : 'hover:bg-slate-50'
                    }`}
                    onClick={() => handleSelectPage(page)}
                  >
                    <div className="flex items-start gap-2">
                      <FileText className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="font-medium text-sm truncate">{page.title || "Untitled"}</span>
                          {page.is_favorite && <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />}
                        </div>
                        <p className="text-xs text-slate-500 truncate mt-0.5">
                          {page.content?.replace(/<[^>]*>/g, '').substring(0, 50) || "Empty"}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-slate-400">{formatDate(page.updated_at)}</span>
                          {page.tags?.length > 0 && (
                            <Badge variant="outline" className="text-xs px-1 py-0">
                              {page.tags[0]}
                              {page.tags.length > 1 && `+${page.tags.length - 1}`}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </div>
          </ScrollArea>
        </div>

        {/* Right - Page Editor */}
        <div className="flex-1 flex flex-col bg-white">
          {selectedPage ? (
            <>
              {/* Page Header */}
              <div className="p-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <Input
                    value={selectedPage.title || ""}
                    onChange={(e) => setSelectedPage(prev => ({ ...prev, title: e.target.value }))}
                    onBlur={(e) => handlePageTitleChange(e.target.value)}
                    className="text-xl font-semibold border-0 shadow-none p-0 h-auto focus-visible:ring-0"
                    placeholder="Untitled"
                  />
                  
                  <div className="flex items-center gap-2">
                    {isSaving && <span className="text-xs text-slate-400">Saving...</span>}
                    
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleFavorite(selectedPage)}
                        >
                          {selectedPage.is_favorite ? (
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          ) : (
                            <StarOff className="w-4 h-4" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{selectedPage.is_favorite ? "Remove from favorites" : "Add to favorites"}</TooltipContent>
                    </Tooltip>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          className="text-red-600"
                          onClick={() => { setDeleteTarget({ type: 'page', id: selectedPage.id, name: selectedPage.title }); setDeleteConfirmOpen(true); }}
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Delete Page
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                
                {/* Tags */}
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {selectedPage.tags?.map(tag => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      <Tag className="w-3 h-3 mr-1" />
                      {tag}
                      <button 
                        className="ml-1 hover:text-red-500"
                        onClick={() => handleUpdatePageTags(selectedPage.id, selectedPage.tags.filter(t => t !== tag))}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                  <Input
                    placeholder="Add tag..."
                    className="w-24 h-6 text-xs"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && tagInput.trim()) {
                        const newTags = [...(selectedPage.tags || []), tagInput.trim()];
                        handleUpdatePageTags(selectedPage.id, newTags);
                        setTagInput("");
                      }
                    }}
                  />
                </div>
                
                <div className="text-xs text-slate-500 mt-2">
                  Last edited {formatDate(selectedPage.updated_at)} by {selectedPage.created_by_name}
                </div>
              </div>

              {/* Formatting Toolbar */}
              <div className="px-4 py-2 border-b border-slate-200 flex items-center gap-1 flex-wrap bg-slate-50">
                {/* Text Formatting */}
                <div className="flex items-center gap-1 border-r border-slate-300 pr-2 mr-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => applyFormatting('bold')}>
                        <Bold className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Bold (Ctrl+B)</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => applyFormatting('italic')}>
                        <Italic className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Italic (Ctrl+I)</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => applyFormatting('underline')}>
                        <Underline className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Underline (Ctrl+U)</TooltipContent>
                  </Tooltip>
                </div>
                
                {/* Lists */}
                <div className="flex items-center gap-1 border-r border-slate-300 pr-2 mr-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => applyFormatting('insertUnorderedList')}>
                        <List className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Bullet List</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => applyFormatting('insertOrderedList')}>
                        <ListOrdered className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Numbered List</TooltipContent>
                  </Tooltip>
                </div>
                
                {/* Headings */}
                <div className="flex items-center gap-1 border-r border-slate-300 pr-2 mr-2">
                  <Select onValueChange={(value) => applyFormatting('formatBlock', value)}>
                    <SelectTrigger className="h-8 w-28 text-xs">
                      <SelectValue placeholder="Heading" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="p">Normal</SelectItem>
                      <SelectItem value="h1">Heading 1</SelectItem>
                      <SelectItem value="h2">Heading 2</SelectItem>
                      <SelectItem value="h3">Heading 3</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Link */}
                <div className="flex items-center gap-1 border-r border-slate-300 pr-2 mr-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => {
                        const url = prompt('Enter URL:');
                        if (url) applyFormatting('createLink', url);
                      }}>
                        <Link className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Insert Link</TooltipContent>
                  </Tooltip>
                </div>
                
                {/* Page Color */}
                <div className="flex items-center gap-1">
                  <span className="text-xs text-slate-500 mr-1">Page:</span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 px-2 gap-1">
                        <div className={`w-4 h-4 rounded border ${getPageColorClasses(selectedPage.page_color || 'white').bg}`} />
                        <Palette className="w-3 h-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <div className="p-2">
                        <p className="text-xs font-medium text-slate-500 mb-2">Page Color</p>
                        <div className="grid grid-cols-4 gap-1">
                          {PAGE_COLORS.map(color => (
                            <button
                              key={color.value}
                              className={`w-6 h-6 rounded border ${color.bg} hover:scale-110 transition-transform ${
                                (selectedPage.page_color || 'white') === color.value ? 'ring-2 ring-purple-500' : ''
                              }`}
                              onClick={() => handleUpdatePageColor(selectedPage.id, color.value)}
                              title={color.label}
                            />
                          ))}
                        </div>
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Page Content Editor - Rich Text */}
              <div 
                className="flex-1 overflow-auto"
                style={{ backgroundColor: getPageColorClasses(selectedPage.page_color || 'white').css }}
              >
                <div
                  ref={editorRef}
                  contentEditable
                  className="min-h-full p-4 outline-none prose prose-sm max-w-none"
                  style={{ backgroundColor: 'transparent' }}
                  onInput={handleEditorInput}
                  dangerouslySetInnerHTML={{ __html: selectedPage.content || '' }}
                  placeholder="Start typing your notes here..."
                />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-slate-500">
                <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">Select a page to start editing</p>
                <p className="text-sm mt-1">Or create a new notebook to get started</p>
              </div>
            </div>
          )}
        </div>

        {/* Dialogs */}
        
        {/* Notebook Dialog */}
        <Dialog open={notebookDialogOpen} onOpenChange={setNotebookDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingNotebook ? "Edit Notebook" : "Create Notebook"}</DialogTitle>
              <DialogDescription>
                {editingNotebook ? "Update your notebook settings" : "Create a new notebook to organize your notes"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={editingNotebook ? handleUpdateNotebook : handleCreateNotebook} className="space-y-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={editingNotebook?.name ?? newNotebook.name}
                  onChange={(e) => editingNotebook 
                    ? setEditingNotebook({ ...editingNotebook, name: e.target.value })
                    : setNewNotebook({ ...newNotebook, name: e.target.value })
                  }
                  placeholder="My Notebook"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex gap-2 flex-wrap">
                  {NOTEBOOK_COLORS.map(color => (
                    <button
                      key={color.value}
                      type="button"
                      className={`w-8 h-8 rounded-full ${color.bg} hover:scale-110 transition-transform ${
                        (editingNotebook?.color ?? newNotebook.color) === color.value ? 'ring-2 ring-offset-2 ring-purple-500' : ''
                      }`}
                      onClick={() => editingNotebook 
                        ? setEditingNotebook({ ...editingNotebook, color: color.value })
                        : setNewNotebook({ ...newNotebook, color: color.value })
                      }
                    />
                  ))}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={editingNotebook?.is_personal ?? newNotebook.is_personal}
                  onCheckedChange={(checked) => editingNotebook 
                    ? setEditingNotebook({ ...editingNotebook, is_personal: checked })
                    : setNewNotebook({ ...newNotebook, is_personal: checked })
                  }
                />
                <Label>Personal Notebook (only you can see)</Label>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setNotebookDialogOpen(false); setEditingNotebook(null); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                  {editingNotebook ? "Save Changes" : "Create Notebook"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Section Dialog */}
        <Dialog open={sectionDialogOpen} onOpenChange={setSectionDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingSection ? "Edit Section" : "Create Section"}</DialogTitle>
              <DialogDescription>
                Sections help organize pages within a notebook
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={editingSection ? handleUpdateSection : handleCreateSection} className="space-y-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={editingSection?.name ?? newSection.name}
                  onChange={(e) => editingSection 
                    ? setEditingSection({ ...editingSection, name: e.target.value })
                    : setNewSection({ ...newSection, name: e.target.value })
                  }
                  placeholder="Section Name"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex gap-2 flex-wrap">
                  {SECTION_COLORS.map(color => (
                    <button
                      key={color.value}
                      type="button"
                      className={`w-6 h-6 rounded ${color.bg} hover:scale-110 transition-transform ${
                        (editingSection?.color ?? newSection.color) === color.value ? 'ring-2 ring-offset-2 ring-purple-500' : ''
                      }`}
                      onClick={() => editingSection 
                        ? setEditingSection({ ...editingSection, color: color.value })
                        : setNewSection({ ...newSection, color: color.value })
                      }
                    />
                  ))}
                </div>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setSectionDialogOpen(false); setEditingSection(null); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                  {editingSection ? "Save Changes" : "Create Section"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Page Dialog */}
        <Dialog open={pageDialogOpen} onOpenChange={setPageDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Page</DialogTitle>
              <DialogDescription>
                Add a new page to "{selectedSection?.name}"
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreatePage} className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={newPage.title}
                  onChange={(e) => setNewPage({ ...newPage, title: e.target.value })}
                  placeholder="Page Title"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Page Color</Label>
                <div className="flex gap-2 flex-wrap">
                  {PAGE_COLORS.map(color => (
                    <button
                      key={color.value}
                      type="button"
                      className={`w-8 h-8 rounded border-2 ${color.bg} hover:scale-110 transition-transform ${
                        newPage.page_color === color.value ? 'ring-2 ring-purple-500' : 'border-slate-200'
                      }`}
                      onClick={() => setNewPage({ ...newPage, page_color: color.value })}
                      title={color.label}
                    />
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Tags (optional)</Label>
                <div className="flex gap-2 flex-wrap mb-2">
                  {newPage.tags.map(tag => (
                    <Badge key={tag} variant="secondary">
                      {tag}
                      <button type="button" className="ml-1" onClick={() => handleRemoveTag(tag)}>
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="Add a tag"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  />
                  <Button type="button" variant="outline" onClick={handleAddTag}>Add</Button>
                </div>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPageDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                  Create Page
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Share Dialog */}
        <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Share Notebook</DialogTitle>
              <DialogDescription>
                Share "{selectedNotebook?.name}" with users or teams
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleShareNotebook} className="space-y-4">
              <div className="space-y-2">
                <Label>Share with Teams</Label>
                <div className="space-y-2">
                  {teams.map(team => (
                    <div key={team} className="flex items-center gap-2">
                      <Checkbox
                        checked={shareSettings.teams.includes(team)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setShareSettings(prev => ({ ...prev, teams: [...prev.teams, team] }));
                          } else {
                            setShareSettings(prev => ({ ...prev, teams: prev.teams.filter(t => t !== team) }));
                          }
                        }}
                      />
                      <span className="text-sm">{team}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {user?.role === 'admin' && allUsers.length > 0 && (
                <div className="space-y-2">
                  <Label>Share with Specific Users</Label>
                  <ScrollArea className="h-40 border rounded-lg p-2">
                    {allUsers.map(u => (
                      <div key={u.id} className="flex items-center gap-2 py-1">
                        <Checkbox
                          checked={shareSettings.users.includes(u.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setShareSettings(prev => ({ ...prev, users: [...prev.users, u.id] }));
                            } else {
                              setShareSettings(prev => ({ ...prev, users: prev.users.filter(id => id !== u.id) }));
                            }
                          }}
                        />
                        <span className="text-sm">{u.name} ({u.team})</span>
                      </div>
                    ))}
                  </ScrollArea>
                </div>
              )}
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShareDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                  <Share2 className="w-4 h-4 mr-2" />
                  Update Sharing
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Lock Dialog */}
        <Dialog open={lockDialogOpen} onOpenChange={setLockDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Lock Notebook</DialogTitle>
              <DialogDescription>
                Set a password to protect "{selectedNotebook?.name}"
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleLockNotebook} className="space-y-4">
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  value={lockPassword}
                  onChange={(e) => setLockPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setLockDialogOpen(false); setLockPassword(""); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                  <Lock className="w-4 h-4 mr-2" />
                  Lock Notebook
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Unlock Dialog */}
        <Dialog open={unlockDialogOpen} onOpenChange={setUnlockDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Unlock Notebook</DialogTitle>
              <DialogDescription>
                Enter password to access "{selectedNotebook?.name}"
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUnlockNotebook} className="space-y-4">
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  value={lockPassword}
                  onChange={(e) => setLockPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setUnlockDialogOpen(false); setLockPassword(""); setSelectedNotebook(null); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                  <Unlock className="w-4 h-4 mr-2" />
                  Unlock
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete {deleteTarget?.type}?</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{deleteTarget?.name}"? 
                {deleteTarget?.type === 'notebook' && " This will delete all sections and pages inside it."}
                {deleteTarget?.type === 'section' && " This will delete all pages inside it."}
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDeleteConfirmOpen(false); setDeleteTarget(null); }}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => {
                  if (deleteTarget?.type === 'notebook') handleDeleteNotebook();
                  else if (deleteTarget?.type === 'section') handleDeleteSection();
                  else if (deleteTarget?.type === 'page') handleDeletePage();
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default NotesPanel;
